import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {FaPlus} from "react-icons/fa";
import Button from "../../../../components/button/button.jsx";
import {useQuery} from "@tanstack/react-query";
import animalsService from "../../../../services/web/animals-service.js";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import {IoMdClose} from "react-icons/io";
import projectUtils from "../../../../utils/project-utils.js";
import AnimalsGenericList from "./components/animals-generic-list.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import ModalImage from "../../../../components/modal/modal-image.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";

const initialState = {
    name: "",
    identification: "",
    status: "",
    dt_birth: "",
    type: "",
    race: "",
    color: "",
    specialty: "",
    origin: "",
    vaccines: [],
    observations: "",
    animal_photo_id: "",
    img: null,
    imgSrc: null,
    procedures: [],
    trainings: []
}

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const AnimalsForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [state, setState] = useState(initialState);
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState([]);
    const [imageModal, setImageModal] = useState({open: false, imgScr: null});

    const [isLoading, setIsLoading] = useState(false)

    const imgRef = useRef(undefined);

    const {data, refetch} = useQuery({
        queryFn: () => animalsService.get(id),
        queryKey: ["animal"],
        enabled: !!id
    });

    useEffect(() => {
        if (!!id && data) {
            setState({
                name: data.name,
                identification: data.identification,
                status: data.status,
                dt_birth: data.dt_birth?.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1'),
                type: data.type,
                race: data.race,
                color: data.color,
                specialty: data.specialty,
                origin: data.origin,
                vaccines: data.vaccines,
                observation: data.observations,
                animal_photo_id: data.animal_photo_id,
                img: null,
                imgSrc: null,
                procedures: data.procedures,
                trainings: data.trainings
            });

            setAttaches(data.attaches);

            const attachesDescription = data.attaches.reduce((acc, a) => {
                acc[a.attachId] = a.description ?? null;
                return acc;
            }, {});

            setAttachesDescription(attachesDescription)
        } else  {
            setState(initialState);
            setAttaches([]);
        }
    }, [id, data]);

    const handleInput = ({target: {id, value}}) => {
        setState(prev => ({...prev, [id]: value}));
    };

    const attachImage = ({target: {files}}) => {
        const file = files[0];
        const reader = new FileReader();

        if (file.size >= 2000000) {
            let message = 'Tamanho máximo de arquivo excedido. Limite: 2MB.';
            projectUtils.swalErrorMessage('', message, 'Ok');
            return;
        }

        reader.onload = function (e) {
            setState(prev => ({...prev, animal_photo_id: null, img: file, imgSrc: e.target.result}));
        }

        reader.readAsDataURL(file);
    }

    const removeFile = () => {
        setState(prev => ({...prev, animal_photo_id: null, imgSrc: null, img: null}));
    }

    const prepareData = () => {
        const formData = new FormData();

        formData.append("name", projectUtils.normalizeData(state.name));
        formData.append("identification", projectUtils.normalizeData(state.identification));
        formData.append("status", projectUtils.normalizeData(state.status));
        formData.append("dt_birth", state.dt_birth instanceof Date ? state.dt_birth?.toLocaleDateString() : state.dt_birth?.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') || "");
        formData.append("type", projectUtils.normalizeData(state.type));
        formData.append("race", projectUtils.normalizeData(state.race));
        formData.append("color", projectUtils.normalizeData(state.color));
        formData.append("specialty", projectUtils.normalizeData(state.specialty));
        formData.append("origin", projectUtils.normalizeData(state.origin));
        formData.append("vaccines", JSON.stringify(state.vaccines));
        formData.append("observations", projectUtils.normalizeData(state.observation));
        formData.append("procedures", JSON.stringify(state.procedures));
        formData.append("trainings", JSON.stringify(state.trainings));
        formData.append("animal_photo_id", state.animal_photo_id ?? "");

        if (state.img) formData.append("img", state.img)

        const attachmentDescriptionNew = [];
        attaches.filter(item => item.new).forEach(item => {
            const attachDescription = attachesDescription[item.attachId];

            if (attachDescription) {
                attachmentDescriptionNew.push({
                    name: item.name,
                    description: attachDescription
                });
            }

            formData.append("attachFiles[]", item);
        });

        formData.append("attachment", JSON.stringify(attaches.filter(item => !item.new)));
        formData.append("attachmentDescription", JSON.stringify(attachmentDescriptionNew));

        return formData
    }

    const submit = () => {
        const formData = prepareData();
        setIsLoading(true);

        let response;
        if (id) {
            formData.append("_method", "PUT")
            response = animalsService.edit(id, formData);
        }
        else response = animalsService.create(formData);

        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/animals/form/${data.id}`;
                navigate(urlDev);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }
                if (error.response.status === 413) {
                    message = "Tamanho máximo de arquivo excedido. Limite: 20MB";
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsLoading(false));
    }

    const openImageModal = (e) => {
        if (e.target.classList.contains("remove-image") || (!state.animal_photo_id && !state.imgSrc)) return

        setImageModal({open: true, imgScr: (state.animal_photo_id && !state.imgSrc) ? `${API_KEEPER}/ged/attach/${state.animal_photo_id}` : state.imgSrc ? state.imgSrc : getAssetPath("img/no-img.png")})
    }

    const setOpenImageModal = (open) => {
        setImageModal(prev => ({...prev, open}))
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title browserTitle="Animal - Cadastro">Animal</Template.Title>

                    {
                        id &&
                        <Button target={"default"} onClick={() => navigate("/animals/form")}>
                            <FaPlus/> Adicionar animal
                        </Button>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Dados</Card.Title>
                        </Card.Header>

                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <section className={"col-span-3"}>
                                <div className={"h-[150px]"}>
                                    <div
                                        onClick={openImageModal}
                                        className={"cursor-pointer bg-no-repeat bg-contain h-full border border-[#e0e0e0] p-1 rounded"}
                                        style={{backgroundImage: (state.animal_photo_id && !state.imgSrc) ? `url(${API_KEEPER}/ged/attach/${state.animal_photo_id})` : state.imgSrc ? `url(${state.imgSrc})` : `url(${getAssetPath("img/no-img.png")})`, backgroundPosition: "50% center"}}
                                    >
                                        {
                                            (state.animal_photo_id || state.imgSrc) &&
                                            <Button className={"remove-image float-right"} target={"danger"} title={"Remover imagem"} onClick={removeFile}>
                                                <IoMdClose className={"remove-image"}/>
                                            </Button>
                                        }
                                    </div>
                                </div>
                                <input type="file" accept="image/png, image/jpeg" className="hidden" name="image" onChange={attachImage} ref={imgRef}/>
                                <Button className={"w-full"} target={"primary"} onClick={() => imgRef.current?.click()}>
                                    Selecionar foto
                                </Button>
                                <small>* Tamanho máximo 2MB</small>
                            </section>

                            <SentryRow className={"col-span-9"}>
                                <InputForm
                                    id={"name"}
                                    classNameWrapper={"col-span-5"}
                                    label={"Nome *"}
                                    value={state.name}
                                    onChange={handleInput}
                                />
                                <InputForm
                                    id={"identification"}
                                    classNameWrapper={"col-span-4"}
                                    label={"Número de identificação"}
                                    value={state.identification}
                                    onChange={handleInput}
                                />
                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    value={state.status}
                                    options={[
                                        {value: "AVAILABLE", label: "Disponível"},
                                        {value: "INACTIVE", label: "Inativo"},
                                        ...(data?.status === "GARNISHED" ? [{value: "GARNISHED", label: "Guarnecido"}] : [])
                                    ]}
                                    label={"Status"}
                                    isDisabled={data?.status === "GARNISHED"}
                                    onChange={(value) => setState(prev => ({...prev, status: value?.value}))}
                                />

                                <InputDateTime
                                    id={"dtBirth"}
                                    label={"Nascimento"}
                                    dateFormat="L"
                                    classNameWrapper={"col-span-3"}
                                    selected={state.dt_birth}
                                    onChange={(date) => setState(prev => ({...prev, dt_birth: date}))}
                                />
                                <InputForm
                                    id={"type"}
                                    classNameWrapper={"col-span-3"}
                                    label={"Tipo"}
                                    value={state.type}
                                    onChange={handleInput}
                                />
                                <InputForm
                                    id={"race"}
                                    classNameWrapper={"col-span-3"}
                                    label={"Raça"}
                                    value={state.race}
                                    onChange={handleInput}
                                />
                                <InputForm
                                    id={"color"}
                                    classNameWrapper={"col-span-3"}
                                    label={"Cor"}
                                    value={state.color}
                                    onChange={handleInput}
                                />

                                <InputForm
                                    id={"origin"}
                                    classNameWrapper={"col-span-6"}
                                    label={"Origem (Doação/Compra/Criação interna)"}
                                    value={state.origin}
                                    onChange={handleInput}
                                />
                                <InputForm
                                    id={"specialty"}
                                    classNameWrapper={"col-span-6"}
                                    label={"Especialidade"}
                                    value={state.specialty}
                                    onChange={handleInput}
                                />

                                <InputTextarea
                                    id={"observation"}
                                    className={"col-span-12"}
                                    label={"Observação"}
                                    value={state.observation}
                                    onChange={handleInput}
                                />
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <AnimalsGenericList
                        state={state}
                        setState={setState}
                        target={"vaccines"}
                    />

                    <AnimalsGenericList
                        state={state}
                        setState={setState}
                        target={"procedures"}
                    />

                    <AnimalsGenericList
                        state={state}
                        setState={setState}
                        target={"trainings"}
                    />

                    <Dropzone
                        title={"Anexos"}
                        attaches={attaches}
                        setAttaches={setAttaches}
                        attachesDescription={attachesDescription}
                        setAttachesDescription={setAttachesDescription}
                    />

                    <Card>
                        <Card.Body className={"flex justify-end"}>
                            <Button onClick={submit} actionFeedbackType='save'>

                                Salvar
                            </Button>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <ModalImage
                open={imageModal.open}
                setOpen={setOpenImageModal}
                imgUrl={imageModal.imgScr}
            />
        </>
    );
};

export default AnimalsForm;